import { useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import { LuChevronDown, LuCheck, LuInfo, LuTriangleAlert } from 'react-icons/lu';
import CxPainel from './CxPainel';

/* Os quatro pré-requisitos da tela antiga, na mesma ordem. O texto é do
   produto — só a moldura mudou (aba inteira → painel de 460px). */
const PREREQS: { titulo: string; aviso: string | null; passos: string[] | null }[] = [
    {
        titulo: 'Criar portfólio no Meta Business',
        aviso: null,
        passos: [
            'Acesse business.facebook.com e faça login',
            'No canto superior esquerdo, clique no menu suspenso',
            'Selecione "Criar um portfólio empresarial"',
            'Digite o nome da empresa (sem caracteres especiais)',
            'Preencha nome, sobrenome e um e-mail comercial',
            'Clique em "Criar" e confirme o e-mail recebido',
        ],
    },
    {
        titulo: 'WhatsApp Business no celular',
        aviso: 'Só se você ainda não usa o WhatsApp Business nesse número. Já usa? Pule para o passo 3.',
        passos: [
            'Instale o "WhatsApp Business" pela App Store ou Google Play',
            'Abra o app e aceite os Termos de Serviço',
            'Insira o número de telefone que será conectado ao CRM',
            'Confirme o número via SMS ou chamada de voz',
            'Preencha o perfil da empresa: nome, categoria e descrição',
        ],
    },
    {
        titulo: 'Forma de pagamento no Meta Business',
        aviso: 'Sem pagamento configurado, o envio de mensagens fica bloqueado (erro 131042).',
        passos: [
            'Acesse o Meta Business Suite',
            'No menu lateral, clique em "Faturamento e pagamentos"',
            'Clique em "Formas de pagamento" → "Adicionar"',
            'Preencha os dados do cartão de crédito e o país',
            'Informe os dados fiscais, se solicitado',
            'Clique em "Avançar" e depois em "Salvar"',
        ],
    },
    {
        titulo: 'Clique em "Configurar API oficial" e siga o passo a passo da Meta',
        aviso: null,
        passos: null,
    },
];

interface Props {
    open: boolean;
    onClose: () => void;
    /** Abre o fluxo da Meta (FB.login). */
    onConectar: () => void;
    conectando: boolean;
    /** SDK do Facebook e configuração da Meta carregados. */
    pronto: boolean;
    /** Uso do plano para o WhatsApp oficial; `limite` null = sem limite. */
    uso: { usado: number; limite: number | null };
}

/**
 * Conectar WhatsApp pela API oficial da Meta — desenho 2.5 da proposta.
 * O que era a aba "WhatsApp" inteira, num painel: nota do parceiro, os
 * pré-requisitos em acordeão e o botão da Meta fixo no rodapé.
 */
const ConectarMetaPanel = ({ open, onClose, onConectar, conectando, pronto, uso }: Props) => {
    const [aberto, setAberto] = useState<number | null>(null);
    const noLimite = uso.limite !== null && uso.usado >= uso.limite;

    return (
        <CxPainel
            open={open}
            onClose={onClose}
            icone={<span className="cx-bx is-wa is-lg" aria-hidden="true"><FaWhatsapp size={18} /></span>}
            title="Conectar WhatsApp"
            subtitle="API oficial da Meta · parceiro oficial GB Code"
            footer={
                <>
                    <button type="button" className="rp-btn" onClick={onClose} disabled={conectando}>Cancelar</button>
                    <button type="button" className="rp-btn is-primary" onClick={onConectar}
                        disabled={conectando || !pronto || noLimite}
                        title={noLimite ? 'Limite de conexões do plano atingido' : !pronto ? 'Carregando a configuração da Meta…' : undefined}>
                        <FaWhatsapp size={13} /> {conectando ? 'Conectando…' : 'Configurar API oficial'}
                    </button>
                </>
            }
        >
            <div className="cx-nota is-info">
                <LuInfo size={14} />
                <span>Você continua usando o WhatsApp Business no celular. A equipe atende e vende pelo CRM, no mesmo número, sem interrupção.</span>
            </div>

            <h3 className="rp-sec-title cx-sec">Antes de conectar</h3>
            <div className="cx-passos">
                {PREREQS.map((p, i) => {
                    const ultimo = p.passos === null;
                    const estaAberto = aberto === i;
                    return (
                        <div key={i} className={`cx-passo ${estaAberto ? 'is-open' : ''}`}>
                            <button type="button" className="cx-passo-h" disabled={ultimo}
                                aria-expanded={!ultimo ? estaAberto : undefined}
                                onClick={() => setAberto(estaAberto ? null : i)}>
                                <span className="cx-passo-n">{i + 1}</span>
                                <span className="cx-passo-t">{p.titulo}</span>
                                {!ultimo && <LuChevronDown size={14} className="cx-passo-chev" />}
                            </button>
                            {estaAberto && p.passos && (
                                <div className="cx-passo-b">
                                    {p.aviso && (
                                        <div className="cx-nota">
                                            <LuTriangleAlert size={14} />
                                            <span>{p.aviso}</span>
                                        </div>
                                    )}
                                    <ol>{p.passos.map((s, j) => <li key={j}>{s}</li>)}</ol>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="rp-kv cx-uso">
                <span className="rp-k">Uso do plano</span>
                <span className="rp-v">
                    {uso.limite === null
                        ? <><LuCheck size={12} /> {uso.usado} {uso.usado === 1 ? 'conexão' : 'conexões'} · sem limite no plano</>
                        : <>{uso.usado} de {uso.limite} {uso.limite === 1 ? 'conexão' : 'conexões'} de WhatsApp
                            {noLimite && <> · <span className="cx-warn">limite atingido</span></>}</>}
                </span>
            </div>
        </CxPainel>
    );
};

export default ConectarMetaPanel;
